/**
 * NeverEmailed: the captures nobody was emailed (admin only: the Rigs
 * console's "Never emailed" tab, after Review). The consent promise: they are
 * listed 30 days after the event and deleted 60 days after it, with the
 * details of everyone never emailed, unless an admin holds one (30 days at a
 * time, with a reason). docs/ingest/16-never-captured-deletion.md section 8.
 *
 * One row per capture on the list: its people (the name from the roster, the
 * number, why each was not emailed), why the capture itself was not sent
 * (processing failed, waiting for review, approved but not sent), its
 * deletion day, and what happens then (deleted, or kept for someone in it who
 * was emailed). Actions: Hold 30 days (a reason is required; allowed once the
 * deletion is at most 30 days away), Fix address (the staff email fix,
 * fixRosterEmail: on a frozen job it saves the address of anyone in a capture
 * and sends no pass; any change clears a guardian confirmation), Send now
 * (the Captures tab's send, when the capture is approved and the address is
 * good), and the guardian check for anyone under 18 (the Review tab's
 * dialog). The rows show before the list's day too, so an address can be
 * fixed while the emails go out; nothing is deleted before the deletion day.
 *
 * Reads the preview (previewNeverEmailedDeletion: counts, numbers, masked
 * emails, never a name) and the roster live (the names); writes only through
 * the admin callables. The deletion itself runs on the server: the daily
 * schedule when an admin switched the job to automatic
 * (deletion.neverEmailedAuto), or the run callable with the preview's counts.
 * Look: the Review tab's (rigv2-review.css), in the console's own tokens.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./rigv2-review.css";
import "./rigv2-unsent.css";
import { useFirestoreCollection } from "../../../../lib/useFirestore.js";
import { rigv2 } from "./rigv2Callables.js";
import { useModalDialog } from "./dialogFocus.js";
import { GuardianDialog } from "./DeliveryReview.jsx";
import { guardianStateOf, reviewErrorText } from "./deliveryReviewModel.js";

export const HOLD_DAYS = 30;
const MAX_REASON = 300;

/* ── dates: New York calendar days, YYYY-MM-DD ── */

export const addDaysIso = (iso, n) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ""));
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n)).toISOString().slice(0, 10);
};
// "Nov 30" (and the year when it is not this plan's year).
export const dayLabel = (iso, thisYear = null) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ""));
  if (!m) return "";
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const opts = { month: "short", day: "numeric", timeZone: "UTC", ...(thisYear && m[1] !== thisYear ? { year: "numeric" } : {}) };
  return d.toLocaleDateString("en-US", opts);
};
const shortTime = (ms) => (ms ? new Date(ms).toLocaleString("en-US", {
  month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York",
}) : "");

/* ── the rows (pure: exported for the test) ── */

const STAGE = {
  processing_failed: ["needs", "Processing failed"],
  waiting_for_review: ["working", "Waiting for review"],
  approved: ["live", "Approved, not sent"],
  photos_only: ["live", "Photos only, not sent"],
  discarded: ["stopped", "Discarded"],
};
export const stageChip = (stage) => STAGE[stage] ?? ["stopped", stage];

/** Why this person was not emailed this capture, in words, and the chip's kind. */
export function memberChip(m, row) {
  // Emailed about this capture's page (their hold keeps it), or only about another capture.
  if (m.status === "emailed") return m.holds ? ["live", "Emailed: keeps the capture"] : ["stopped", "Emailed about another capture"];
  if (m.status === "sending") return ["working", "Email on its way"];
  if (m.status === "ended" || m.status === "deleted") return ["stopped", m.status === "deleted" ? "Deleted" : "Page ended"];
  if (m.addressIssue === "no_address") return ["needs", "No email address"];
  if (m.addressIssue === "needs_guardian") return ["needs", "Under 18: guardian check"];
  // The provider refused the last email about it (a bad address, say): nothing reached them.
  if (m.lastSend?.status === "failed") return ["error", "Email refused"];
  if (row.stage === "approved" || row.stage === "photos_only") return ["needs", "Not sent yet"];
  return ["stopped", "Never emailed"];
}

// The staff email fix's answer (fixRosterEmail: { emailMasked, emailed, passNotSent? }), in words.
export function fixResultText(res) {
  const addr = res?.emailMasked || "the new address";
  if (res?.passNotSent === "capture_frozen") return `Address saved: ${addr}. No pass was sent (captures on this job are closed).`;
  return res?.emailed ? `Address saved: ${addr}. The pass was emailed there too.` : `Address saved: ${addr}.`;
}
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The list's rows with each person's name from the live roster (the preview carries none). */
export function rowsOf(plan, roster) {
  const byId = Object.fromEntries((roster ?? []).map((r) => [r.id, r]));
  return (plan?.captures ?? []).map((row) => {
    const members = (row.members ?? []).map((m) => {
      const entry = byId[m.rosterEntryId] ?? null;
      const number = m.displayId ? `#${String(m.displayId).replace(/^#/, "")}` : "";
      return {
        ...m, entry, number,
        name: (entry?.name ?? "").trim() || number || "Unnamed",
        guardian: entry && m.addressIssue === "needs_guardian" ? guardianStateOf(entry) : "adult",
      };
    });
    const title = members.length > 1 ? `${members[0].name} +${members.length - 1}` : (members[0]?.name ?? "Unnamed");
    return { ...row, members, title };
  });
}

/**
 * Whether a hold can be placed now, and what it would set (the server's own
 * rule, holdWindow): once the capture's deletion day is at most 30 days away,
 * 30 days after that day (after today, if it has passed).
 */
export function holdStateOf(row, today) {
  const current = row?.deadline ?? null;
  if (!current || !today) return { can: false, renewFrom: null, until: null };
  const renewFrom = addDaysIso(current, -HOLD_DAYS);
  if (today < renewFrom) return { can: false, renewFrom, until: null };
  return { can: true, renewFrom, until: addDaysIso(current > today ? current : today, HOLD_DAYS) };
}

/** Someone in the capture was never emailed: their details go on its day, unless it is held. */
export const hasNeverEmailed = (row) => (row?.members ?? []).some((m) => m.status === "never");

// Why a person's details stay past the day (the server's keep reasons), in words.
const KEEP_WORDS = {
  held: "a capture of theirs is held",
  capture_not_finished: "a capture of theirs is still under way",
  email_on_its_way: "an email in one of their captures is on its way",
  held_registration: "their registration is on hold",
  has_file: "a file is stored with their registration",
};

// Send now's answer (sendGuestDelivery: { status: "sent" | "unconfirmed" | "failed", emailMasked }), in words.
export function sendResultText(r) {
  if (r?.status === "failed") return "The email was refused. Check the address.";
  if (r?.status === "unconfirmed") return `The email may have gone to ${r?.emailMasked || "them"}; the provider did not confirm it.`;
  return `Emailed ${r?.emailMasked ?? ""}.`;
}

/**
 * What happens to a never-emailed person's details, from the plan's own row
 * for them (their day is the latest of their captures' days, and something
 * may keep them), never from one capture's day.
 */
export function personDetailsText(p, year = null) {
  if (!p) return "";
  // Their deletion record (personTombstones) names someone else: only by hand, so staff fix it by hand.
  if (p.keep === "scrub_record_mismatch") {
    return "Needs a fix: their deletion record names someone else (changed by hand). Delete that record in the Firebase console; " +
      "the next run makes theirs.";
  }
  if (p.keep) return `Details kept: ${KEEP_WORDS[p.keep] ?? "held"}`;
  if (!p.scrub) return "";
  return p.deadline ? `Details deleted ${dayLabel(p.deadline, year)}` : "";
}
const personOf = (plan, id) => (plan?.people ?? []).find((p) => p.rosterEntryId === id) ?? null;

/** Whether the row offers Hold 30 days: the capture goes on its day, or someone never emailed in it does. */
export const offersHold = (row) => row?.fate === "delete" || hasNeverEmailed(row);

/** What happens to the capture on its day, in words, and the chip's kind. */
export function fateChip(row, plan) {
  const year = plan?.nyToday?.slice(0, 4) ?? null;
  if (row.fate === "kept_emailed") return ["live", "Stays: someone in it was emailed"];
  if (row.fate === "kept_sending") return ["working", "Stays for now: an email is on its way"];
  const held = row.hold && row.hold.untilDay > (plan?.asOf ?? "");
  if (held) return ["paused", `Held: deleted ${dayLabel(row.deadline, year)}`];
  return ["needs", row.due && plan?.datePassed ? "Deleted at the next run" : `Deleted ${dayLabel(row.deadline, year)}`];
}

// The callables' refusals, in words.
export function neverEmailedErrorText(e) {
  const reason = e?.details?.reason ?? "";
  if (reason === "too_early") {
    const from = e?.details?.renewFrom;
    return from ? `It can be held from ${dayLabel(from)} (30 days before its deletion).` : "Not yet: its deletion is more than 30 days away.";
  }
  if (reason === "dates_unknown") return "The job's dates cannot be read, so its deletion day is not known. Fix the job's dates first.";
  if (reason === "deleted") return "This capture was deleted.";
  const code = String(e?.message || "");
  if (code === "already_sent") return "Already emailed.";
  if (code === "no_email") return "No email on file. Fix the address first, then send.";
  if (/Invalid email address/.test(code)) return "That is not an email address.";
  if (/Captures on this job are closed/.test(code)) return "The address was not changed: this person is in no capture of this job.";
  if (/was deleted/.test(code)) return "This registration was deleted.";
  if (code === "needs_guardian") return "Check the parent or guardian's address first.";
  if (code === "not_approved") return "Approve the model (or Photos only) on the Review tab first.";
  if (code === "ended") return "Their page has ended.";
  return reviewErrorText(e);
}

/* ── small pieces ── */

function StatusChip({ kind, children }) {
  return <span className={`rv-status ${kind}`}><i aria-hidden="true" />{children}</span>;
}

function Dialog({ label, onClose, children }) {
  const frameRef = useRef(null);
  useModalDialog(frameRef);
  return (
    <div className="rigv2-modal-scrim rv-scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rv-dialog" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} ref={frameRef}
        onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
        {children}
      </div>
    </div>
  );
}

export function FixAddressDialog({ member, busy = false, error = "", onConfirm, onClose }) {
  const [email, setEmail] = useState("");
  const ok = EMAIL_RE.test(email.trim());
  return (
    <Dialog label="Fix the email address" onClose={onClose}>
      <p className="rv-dialog-title">Fix {member?.name ?? "their"}'s email address</p>
      <p className="rv-dialog-sub">
        Saves the address for their delivery{member?.emailMasked ? ` (now ${member.emailMasked})` : ""}. On a job whose captures are
        closed no pass is sent; while captures are open, their pass is emailed to the new address too. For a guest under 18, check the
        parent or guardian's address again afterwards.
      </p>
      <label className="rv-field">
        <span className="rv-label">New email address</span>
        <input className="rv-input" type="email" autoComplete="off" value={email} maxLength={254} onChange={(e) => setEmail(e.target.value)} />
      </label>
      {error && <p className="rv-hint warn">{error}</p>}
      <div className="rv-dialog-actions">
        <button type="button" className="rigv2-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="rigv2-btn primary rv-primary" disabled={!ok || busy} onClick={() => onConfirm(email.trim())}>
          {busy ? "Saving…" : "Save the address"}
        </button>
      </div>
    </Dialog>
  );
}

export function HoldDialog({ row, plan, busy = false, error = "", onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  const year = plan?.nyToday?.slice(0, 4) ?? null;
  const w = holdStateOf(row, plan?.nyToday);
  const ok = reason.trim().length >= 3;
  return (
    <Dialog label="Hold this capture 30 more days" onClose={onClose}>
      <p className="rv-dialog-title">Hold this capture 30 more days?</p>
      <p className="rv-dialog-sub">
        {row.fate === "delete" ? "It and everyone" : "Everyone"} never emailed in it stay{row.fate === "delete" ? "" : "s"} until {dayLabel(w.until, year)} instead of {dayLabel(row.deadline, year)}.
        You can hold it again once that day is 30 days away. The reason is kept with every hold.
      </p>
      <label className="rv-field">
        <span className="rv-label">Why (required)</span>
        <input className="rv-input" value={reason} maxLength={MAX_REASON} onChange={(e) => setReason(e.target.value)}
          placeholder="Waiting for the guest's address from the producer" />
      </label>
      {error && <p className="rv-hint warn">{error}</p>}
      <div className="rv-dialog-actions">
        <button type="button" className="rigv2-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="rigv2-btn primary rv-primary" disabled={!ok || busy} onClick={() => onConfirm(reason.trim())}>
          {busy ? "Saving…" : "Hold 30 days"}
        </button>
      </div>
    </Dialog>
  );
}

/* ── the view (pure render: exported for the test) ── */

export function NeverEmailedView({ plan, roster, busy = {}, notes = {}, notice = "", onHold, onEmail, onFix, onGuardian, onRefresh, loading = false, error = "" }) {
  const rows = useMemo(() => rowsOf(plan, roster), [plan, roster]);
  const year = plan?.nyToday?.slice(0, 4) ?? null;
  const c = plan?.counts ?? {};
  const byId = Object.fromEntries((roster ?? []).map((r) => [r.id, r]));
  const alone = (plan?.people ?? []).filter((p) => !p.captures?.length && p.status === "never");
  const blockers = (plan?.blockers ?? []).filter((b) => b.code !== "too_early");
  return (
    <div className="rigv2-review rigv2-unsent">
      <div className="rv-head">
        <div className="rv-head-left">
          <span className="rv-eyebrow">Never emailed</span>
          <span className="rv-sub">
            {plan?.deleteFrom
              ? <>Captures nobody was emailed are deleted from {dayLabel(plan.deleteFrom, year)}, 60 days after the event, with the details of
                everyone never emailed. Email them now, or hold a capture 30 more days if you need time.</>
              : "The job's dates cannot be read, so no deletion day is known yet."}
          </span>
        </div>
        <button type="button" className="rigv2-btn small" disabled={loading} onClick={onRefresh}>{loading ? "Loading…" : "Refresh"}</button>
      </div>

      {error && <div className="rv-banner error" role="status">{error}</div>}
      {/* The last action's answer, kept here when the refreshed list no longer shows its row. */}
      {notice && <div className="rv-banner" role="status">{notice}</div>}

      {plan && (
        <div className="ne-dates">
          <span className="ne-date"><span className="k">Event ended</span><span className="v">{dayLabel(plan.lastEventDate, year) || "unknown"}</span></span>
          <span className="ne-date"><span className="k">Listed from</span><span className="v">{dayLabel(plan.listFrom, year) || "unknown"}</span></span>
          <span className="ne-date"><span className="k">Deleted from</span><span className="v">{dayLabel(plan.deleteFrom, year) || "unknown"}</span></span>
          <span className="ne-date"><span className="k">Today</span><span className="v">{dayLabel(plan.nyToday, year)}</span></span>
          <span className="ne-date"><span className="k">Automatic</span><span className="v">{plan.auto ? "On" : "Off"}</span></span>
        </div>
      )}

      {plan && blockers.length > 0 && (
        <div className="rv-banner" role="status">
          <span className="ne-blockers">
            <span className="rv-label">Nothing is deleted while these stand:</span>
            {blockers.map((b) => <span key={b.code}>{b.message}</span>)}
          </span>
        </div>
      )}

      {plan && (
        <div className="rv-strip ne-strip">
          <span className="rv-stat"><span className="v">{c.capturesNeverEmailed ?? 0}</span><span className="l">Captures listed</span></span>
          <span className="rv-stat"><span className="v">{c.peopleNeverEmailed ?? 0}</span><span className="l">People never emailed</span></span>
          <span className="rv-stat"><span className="v">{plan.toDeleteCaptures?.length ?? 0}</span><span className="l">Captures to delete</span></span>
          <span className="rv-stat"><span className="v">{plan.toDeletePeople?.length ?? 0}</span><span className="l">People to delete</span></span>
          <span className="rv-stat"><span className="v">{c.capturesHeld ?? 0}</span><span className="l">Held</span></span>
        </div>
      )}

      {plan && (c.emailedWithoutPage ?? 0) > 0 && (
        <div className="rv-banner" role="status">
          {c.emailedWithoutPage} {c.emailedWithoutPage === 1 ? "person has" : "people have"} no guest page, but a capture's record says an
          email may have reached them. Their details, and the captures their record holds, stay until the missing page is repaired by hand.
        </div>
      )}

      {plan && !plan.listed && (
        <div className="rv-banner" role="status">
          The list is due on {dayLabel(plan.listFrom, year) || "the 30th day after the event"}. It shows already what nobody was emailed yet,
          so an address can be fixed while the emails go out; nothing is deleted before {dayLabel(plan.deleteFrom, year) || "the 60th day"}.
        </div>
      )}

      {!plan ? (
        <div className="rigv2-empty">{loading ? "Loading…" : "No preview yet."}</div>
      ) : rows.length === 0 && alone.length === 0 ? (
        <div className="rv-empty">
          <p className="rv-empty-title">Nothing waiting under the 60-day rule</p>
          <p>
            No capture on this job is waiting to be deleted under the 60-day rule.
            {c.peopleSending ? ` ${c.peopleSending} ${c.peopleSending === 1 ? "email is" : "emails are"} on the way.` : ""}
          </p>
        </div>
      ) : (
        <div className="ne-list">
          {rows.map((row) => {
            const [sk, sl] = stageChip(row.stage);
            const [fk, fl] = fateChip(row, plan);
            const hs = holdStateOf(row, plan.nyToday);
            const b = busy[row.sn];
            return (
              <div key={row.sn} className="ne-row" role="group" aria-label={`${row.title}, ${sl}`}>
                <div className="ne-main">
                  <div className="rv-row">
                    <span className="rv-name">{row.title}</span>
                    <span className="rv-sn mono" title={row.sn}>…{row.sn.slice(-4)}</span>
                  </div>
                  <span className="rv-meta">{[shortTime(row.capturedAt), row.detail].filter(Boolean).join(" · ")}</span>
                  <span className="ne-chips"><StatusChip kind={sk}>{sl}</StatusChip></span>
                  <span className="rv-members">
                    {row.members.map((m) => {
                      const [mk, ml] = memberChip(m, row);
                      const mb = busy[`${row.sn}:${m.rosterEntryId}`];
                      const details = m.status === "never" ? personDetailsText(personOf(plan, m.rosterEntryId), year) : "";
                      return (
                        <span key={m.rosterEntryId} className="rv-member">
                          <span>{m.name}{m.number && m.number !== m.name ? ` ${m.number}` : ""}</span>
                          {m.emailMasked && <span className="rv-muted mono">{m.emailMasked}</span>}
                          <StatusChip kind={mk}>{ml}</StatusChip>
                          {details && <span className="rv-muted">{details}</span>}
                          {m.status === "never" && m.lastSend?.status === "failed" && m.lastSend.lastError && (
                            <span className="rv-muted mono">{m.lastSend.lastError}</span>
                          )}
                          {m.canEmail && onEmail && (
                            <button type="button" className="rv-link" disabled={Boolean(mb)} onClick={() => onEmail(row, m)}>
                              {mb === "email" ? "Sending…" : "Send now"}
                            </button>
                          )}
                          {m.status === "never" && m.entry && onFix && (
                            <button type="button" className="rv-link" disabled={Boolean(mb)} onClick={() => onFix(row, m)}>Fix address</button>
                          )}
                          {m.guardian === "needed" && onGuardian && (
                            <button type="button" className="rv-link" onClick={() => onGuardian({ ...m, rid: m.rosterEntryId, entry: byId[m.rosterEntryId] })}>
                              Check the address
                            </button>
                          )}
                          {notes[`${row.sn}:${m.rosterEntryId}`] && <span className="rv-note">{notes[`${row.sn}:${m.rosterEntryId}`]}</span>}
                        </span>
                      );
                    })}
                  </span>
                </div>
                <div className="ne-side">
                  <StatusChip kind={fk}>{fl}</StatusChip>
                  {row.hold && (
                    <span className="rv-muted">
                      Held until {dayLabel(row.hold.untilDay, year)}{row.hold.times > 1 ? ` (${row.hold.times} holds)` : ""}: {row.hold.reason}
                    </span>
                  )}
                  {offersHold(row) && onHold && (
                    <button type="button" className="rigv2-btn small" disabled={!hs.can || Boolean(b)}
                      title={hs.can ? "" : `It can be held from ${dayLabel(hs.renewFrom, year)}.`}
                      onClick={() => onHold(row)}>
                      {b === "hold" ? "Holding…" : "Hold 30 days"}
                    </button>
                  )}
                  {offersHold(row) && !hs.can && hs.renewFrom && <span className="rv-muted">Hold from {dayLabel(hs.renewFrom, year)}</span>}
                  {notes[row.sn] && <span className="rv-note">{notes[row.sn]}</span>}
                </div>
              </div>
            );
          })}
          {alone.length > 0 && (
            <div className="ne-row">
              <div className="ne-main">
                <span className="rv-name">People never emailed whose captures are already gone</span>
                <span className="rv-members">
                  {alone.map((p) => {
                    const entry = byId[p.rosterEntryId];
                    const number = p.displayId ? `#${p.displayId}` : "";
                    return (
                      <span key={p.rosterEntryId} className="rv-member">
                        <span>{(entry?.name ?? "").trim() || number || "Unnamed"}{number && entry?.name ? ` ${number}` : ""}</span>
                        <StatusChip kind={p.keep ? "paused" : "needs"}>{personDetailsText(p, year) || "No details on file"}</StatusChip>
                      </span>
                    );
                  })}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {plan && (
        <p className="rv-footnote">
          On its day the deletion runs by itself only when the job is switched to automatic (deletion.neverEmailedAuto on the job,
          in the Firebase console); it deletes a capture's photographs and models everywhere online, and each person's page and
          registration details. A capture shared with someone who was emailed stays for them until their own dates.
        </p>
      )}
    </div>
  );
}

/* ── the screen ── */

export default function NeverEmailed({ jobId }) {
  const { data: roster } = useFirestoreCollection(jobId ? `jobs/${jobId}/rosterEntries` : null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState({});
  const [notes, setNotes] = useState({});
  const [notice, setNotice] = useState("");
  const [dialog, setDialog] = useState(null);
  const live = useRef(true);
  useEffect(() => () => { live.current = false; }, []);

  const load = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const p = await rigv2.previewNeverEmailedDeletion({ jobId });
      if (live.current) {
        setPlan(p);
        setError("");
      }
    } catch (e) {
      if (live.current) setError(`Could not load the list. ${neverEmailedErrorText(e)}`);
    } finally {
      if (live.current) setLoading(false);
    }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const hold = async (reason) => {
    const row = dialog?.row;
    if (!row) return;
    setDialog((d) => ({ ...d, busy: true, error: "" }));
    setBusy((b) => ({ ...b, [row.sn]: "hold" }));
    try {
      const r = await rigv2.holdNeverEmailedCapture({ jobId, sn: row.sn, reason });
      setNotes((n) => ({ ...n, [row.sn]: `Held until ${dayLabel(r.untilDay)}.` }));
      setDialog(null);
      await load();
    } catch (e) {
      setDialog((d) => (d ? { ...d, busy: false, error: neverEmailedErrorText(e) } : d));
    } finally {
      setBusy((b) => ({ ...b, [row.sn]: null }));
    }
  };

  const email = async (row, m) => {
    const key = `${row.sn}:${m.rosterEntryId}`;
    setBusy((b) => ({ ...b, [key]: "email" }));
    setNotes((n) => ({ ...n, [key]: "" }));
    try {
      const r = await rigv2.sendGuestDelivery({ jobId, sn: row.sn, rosterEntryId: m.rosterEntryId, firstSendOnly: true });
      setNotes((n) => ({ ...n, [key]: sendResultText(r) }));
      // Also above the list: once emailed, the row leaves it.
      setNotice(`${m.name}${m.number && m.number !== m.name ? ` ${m.number}` : ""}: ${sendResultText(r)}`);
      await load();
    } catch (e) {
      setNotes((n) => ({ ...n, [key]: neverEmailedErrorText(e) }));
    } finally {
      setBusy((b) => ({ ...b, [key]: null }));
    }
  };

  const fix = async (email) => {
    const row = dialog?.row;
    const m = dialog?.member;
    if (!row || !m) return;
    const key = `${row.sn}:${m.rosterEntryId}`;
    setDialog((d) => ({ ...d, busy: true, error: "" }));
    setBusy((b) => ({ ...b, [key]: "fix" }));
    try {
      const r = await rigv2.fixRosterEmail({ jobId, rosterEntryId: m.rosterEntryId, email });
      setNotes((n) => ({ ...n, [key]: fixResultText(r) }));
      setDialog(null);
      await load();
    } catch (e) {
      setDialog((d) => (d ? { ...d, busy: false, error: neverEmailedErrorText(e) } : d));
    } finally {
      setBusy((b) => ({ ...b, [key]: null }));
    }
  };

  const confirmGuardian = async ({ address, how }) => {
    const member = dialog?.member;
    if (!member) return;
    setDialog((d) => ({ ...d, busy: true, error: "" }));
    try {
      await rigv2.confirmGuardianRecipient({ jobId, rosterEntryId: member.rid, address, how });
      setDialog(null);
      await load();
    } catch (e) {
      setDialog((d) => (d ? { ...d, busy: false, error: reviewErrorText(e) } : d));
    }
  };

  return (
    <>
      <NeverEmailedView
        plan={plan} roster={roster} busy={busy} notes={notes} notice={notice} loading={loading} error={error}
        onRefresh={load}
        onHold={(row) => setDialog({ kind: "hold", row, busy: false, error: "" })}
        onEmail={email}
        onFix={(row, member) => setDialog({ kind: "fix", row, member, busy: false, error: "" })}
        onGuardian={(member) => setDialog({ kind: "guardian", member, busy: false, error: "" })}
      />
      {dialog?.kind === "fix" && (
        <FixAddressDialog member={dialog.member} busy={dialog.busy} error={dialog.error} onConfirm={fix} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === "hold" && (
        <HoldDialog row={dialog.row} plan={plan} busy={dialog.busy} error={dialog.error} onConfirm={hold} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === "guardian" && (
        <GuardianDialog member={dialog.member} busy={Boolean(dialog.busy)} error={dialog.error}
          onConfirm={confirmGuardian} onClose={() => setDialog(null)} />
      )}
    </>
  );
}
